/**
 * useUrlJobSync — ADR-095 Phase 3.3.
 *
 * Single source of truth for the active-job drawer: `?job=<jobId>` query
 * param on the current URL. Mirrors the URL-canonical pattern from Phase
 * 3.2 (useUrlConversationSync) but for the search-params dimension
 * instead of path params.
 *
 * Consumers (JobsSection):
 *   - `activeJobUrlId`: the current `?job=` value (or null)
 *   - `openJob(jobId)`: set `?job=jobId` (preserves all other search params)
 *   - `closeJob()`: remove `?job=` (preserves all other search params)
 *
 * Cross-phase composition: `?job=` lives alongside the path `/c/:conversationId`
 * from Phase 3.2 and the future `#m_msg_xyz` hash from Phase 3.4. All three
 * coexist in a single URL like `/c/abc?job=xyz#m_msg_123` so deep-links
 * carry the full UI state.
 */

import { useSearchParams } from "react-router-dom";

export function useUrlJobSync(): {
  activeJobUrlId: string | null;
  openJob: (jobId: string) => void;
  closeJob: () => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeJobUrlId = searchParams.get("job");

  function openJob(jobId: string): void {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("job", jobId);
        return next;
      },
      { replace: false },
    );
  }

  function closeJob(): void {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("job");
        return next;
      },
      { replace: false },
    );
  }

  return { activeJobUrlId, openJob, closeJob };
}
